/* =====================================================
   DARUSSALAM CART DRAWER  v2  — assets/darussalam-cart.js
   ===================================================== */

(function () {
  'use strict';

  const drawerEl = () => document.querySelector('cart-drawer.dsc-drawer');

  /* ── 1. FORCE-OPEN: hijack any link to /cart ─────────
     Bug fix: theme nav was navigating to /cart instead of
     opening the drawer. We capture every cart link click
     and call the drawer's open() method.
  ─────────────────────────────────────────────────────── */
  function bindCartTriggers() {
    document.addEventListener('click', function (e) {
      const trigger = e.target.closest(
        'a[href$="/cart"], a[href*="/cart?"], ' +
        'a[href="#cart"], [data-cart-drawer-toggle], ' +
        '.header__icon--cart, #cart-icon-bubble, .cart-link, ' +
        'a[aria-label*="cart" i], a[aria-label*="basket" i]'
      );
      if (!trigger) return;
      // Allow if user explicitly wants to navigate (cmd/ctrl-click, middle click)
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;

      const dr = drawerEl();
      if (!dr) return;

      e.preventDefault();
      e.stopPropagation();
      openDrawer(dr);
    }, true); // capture phase: beat theme handlers
  }

  function openDrawer(dr) {
    if (typeof dr.open === 'function') {
      dr.open();
    } else {
      dr.setAttribute('open', '');
      dr.classList.add('active');
      document.body.classList.add('overflow-hidden');
    }
  }

  function closeDrawer(dr) {
    if (typeof dr.close === 'function') {
      dr.close();
    } else {
      dr.removeAttribute('open');
      dr.classList.remove('active');
      document.body.classList.remove('overflow-hidden');
    }
  }

  /* ── 3. REMOVE ANIMATION ─────────────────────────── */
  function bindRemoveAnimations() {
    document.querySelectorAll('.dsc-item__remove').forEach(btn => {
      if (btn.dataset.dsBound === '1') return;
      btn.dataset.dsBound = '1';
      btn.addEventListener('click', function () {
        const li = btn.closest('.dsc-item');
        if (li) li.classList.add('is-removing');
      }, { capture: true });
    });
  }

  /* ── 4. PAIR-WITH CAROUSEL: scroll snap + arrows ─── */
  function bindPairCarousel() {
    const list = document.querySelector('[data-pair-list]');
    const prev = document.querySelector('[data-pair-prev]');
    const next = document.querySelector('[data-pair-next]');
    if (!list || list.dataset.dsBound === '1') return;
    list.dataset.dsBound = '1';

    function getStep() {
      const item = list.querySelector('.dsc-pair__item');
      return item ? item.getBoundingClientRect().width + 0 : list.clientWidth;
    }

    function updateNavState() {
      if (!prev || !next) return;
      prev.disabled = list.scrollLeft <= 4;
      next.disabled = list.scrollLeft + list.clientWidth >= list.scrollWidth - 4;
    }

    if (prev) {
      prev.addEventListener('click', () => {
        list.scrollBy({ left: -getStep(), behavior: 'smooth' });
      });
    }
    if (next) {
      next.addEventListener('click', () => {
        list.scrollBy({ left: getStep(), behavior: 'smooth' });
      });
    }
    list.addEventListener('scroll', updateNavState, { passive: true });
    updateNavState();

    // Drag-to-scroll
    let isDown = false, startX = 0, scrollLeft = 0;
    list.addEventListener('mousedown', e => {
      isDown = true;
      startX = e.pageX - list.offsetLeft;
      scrollLeft = list.scrollLeft;
      list.style.cursor = 'grabbing';
    });
    ['mouseleave', 'mouseup'].forEach(ev =>
      list.addEventListener(ev, () => {
        isDown = false;
        list.style.cursor = '';
      })
    );
    list.addEventListener('mousemove', e => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - list.offsetLeft;
      list.scrollLeft = scrollLeft - (x - startX) * 1.6;
    });
  }

  /* ── 5. PAIR-WITH AJAX ADD ─────────────────────── */
  function bindPairAdd() {
    document.querySelectorAll('[data-pair-form]').forEach(form => {
      if (form.dataset.dsBound === '1') return;
      form.dataset.dsBound = '1';

      form.addEventListener('submit', function (e) {
        e.preventDefault();
        const btn = form.querySelector('.dsc-pair__add');
        if (!btn || btn.disabled) return;

        const id = form.querySelector('input[name="id"]').value;
        const qty = form.querySelector('input[name="quantity"]').value || 1;

        btn.classList.add('is-loading');
        btn.disabled = true;

        const body = JSON.stringify({
          items: [{ id: parseInt(id, 10), quantity: parseInt(qty, 10) }],
          sections: getSectionsToBundle()
        });

        fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: body
        })
        .then(r => r.json())
        .then(data => {
          btn.classList.remove('is-loading');
          btn.classList.add('is-done');

          // Refresh the drawer using the existing CartDrawer custom element
          refreshDrawer(data);

          setTimeout(() => {
            btn.classList.remove('is-done');
            btn.disabled = false;
          }, 2000);
        })
        .catch(() => {
          btn.classList.remove('is-loading');
          btn.disabled = false;
        });
      });
    });
  }

  function getSectionsToBundle() {
    const dr = drawerEl();
    if (dr && typeof dr.getSectionsToRender === 'function') {
      return dr.getSectionsToRender().map(s => s.id);
    }
    return ['cart-drawer', 'cart-icon-bubble', 'cart-icon-bubble-sticky'];
  }

  function refreshDrawer(addData) {
    const dr = drawerEl();
    if (!dr) return;

    // NOTE: dr.renderContents() (Dawn's native method) expects a /cart/change.js-shaped
    // response (full cart with items[]). /cart/add.js's response has a different shape
    // (single added line + optional sections), which confuses renderContents into firing
    // a stray /cart/change.js call with an invalid line index — surfaced as "line
    // parameter is invalid" in #CartDrawer-CartErrors, and left the drawer's native
    // cart-items element stuck (blocking ALL further add-to-cart on the page). Always
    // use the safe fallback below instead of the native method for add.js-sourced calls.
    fetch(window.location.pathname + '?sections=cart-drawer')
      .then(r => r.json())
      .then(payload => {
        const html = payload['cart-drawer'];
        if (!html) return;
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        const freshInner = tmp.querySelector('#CartDrawer');
        const currentInner = dr.querySelector('#CartDrawer');
        if (!freshInner || !currentInner) return;
        currentInner.innerHTML = freshInner.innerHTML;
        dr.classList.remove('is-empty');
        rebindAll();
        openDrawer(dr);
      })
      .catch(() => {});
  }

  /* ── 5b. COMPLETE YOUR LIBRARY: checkboxes + bulk add ── */
  function updateCylButton() {
    const section = document.querySelector('[data-dsc-cyl]');
    if (!section) return;
    const btn = section.querySelector('[data-cyl-add]');
    if (!btn) return;
    const checked = section.querySelectorAll('[data-cyl-check]:checked');
    btn.disabled = checked.length === 0;
  }

  function bindCylCheckboxes() {
    document.querySelectorAll('[data-cyl-check]').forEach(cb => {
      if (cb.dataset.dsBound === '1') return;
      cb.dataset.dsBound = '1';
      // stopPropagation: the theme's native cart-quantity listener treats ANY
      // change event inside the cart form as a quantity update. Without this,
      // checking a box (value defaults to "on") fires a bogus /cart/change
      // request and shows "There was an error while updating your cart."
      cb.addEventListener('change', function (e) {
        e.stopPropagation();
        updateCylButton();
      });
    });
    updateCylButton();
  }

  function bindCylAdd() {
    const btn = document.querySelector('[data-cyl-add]');
    if (!btn || btn.dataset.dsBound === '1') return;
    btn.dataset.dsBound = '1';

    btn.addEventListener('click', function () {
      const checked = document.querySelectorAll('[data-cyl-check]:checked');
      if (!checked.length || btn.disabled) return;

      const items = Array.from(checked).map(cb => ({
        id: parseInt(cb.dataset.variantId, 10),
        quantity: 1
      }));

      btn.classList.add('is-loading');
      btn.disabled = true;

      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ items: items, sections: getSectionsToBundle() })
      })
      .then(r => r.json())
      .then(data => {
        btn.classList.remove('is-loading');
        btn.classList.add('is-done');
        refreshDrawer(data);
        setTimeout(() => { btn.classList.remove('is-done'); }, 2000);
      })
      .catch(() => {
        btn.classList.remove('is-loading');
        btn.disabled = false;
      });
    });
  }

  /* ── 5c. COMPLETE YOUR LIBRARY: single-suggestion 1-tap add ── */
  function bindCylSingleAdd() {
    document.querySelectorAll('[data-cyl-single-add]').forEach(btn => {
      if (btn.dataset.dsBound === '1') return;
      btn.dataset.dsBound = '1';

      btn.addEventListener('click', function () {
        if (btn.disabled) return;

        const id = btn.dataset.variantId;

        btn.classList.add('is-loading');
        btn.disabled = true;

        fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            items: [{ id: parseInt(id, 10), quantity: 1 }],
            sections: getSectionsToBundle()
          })
        })
        .then(r => r.json())
        .then(data => {
          btn.classList.remove('is-loading');
          btn.classList.add('is-done');
          refreshDrawer(data);
          setTimeout(() => {
            btn.classList.remove('is-done');
            btn.disabled = false;
          }, 2000);
        })
        .catch(() => {
          btn.classList.remove('is-loading');
          btn.disabled = false;
        });
      });
    });
  }


  /* ── 6. RE-BIND on cart updates ───────────────────── */
  function rebindAll() {
    bindRemoveAnimations();
    bindPairCarousel();
    bindPairAdd();
    bindCylCheckboxes();
    bindCylAdd();
    bindCylSingleAdd();
  }

  function observeCart() {
    const target = document.querySelector('.dsc-cart-drawer');
    if (!target) return;
    const ob = new MutationObserver(() => {
      clearTimeout(ob._t);
      ob._t = setTimeout(rebindAll, 80);
    });
    ob.observe(target, { childList: true, subtree: true });
  }

  /* ── 7. INIT ──────────────────────────────────────── */
  function init() {
    bindCartTriggers();
    rebindAll();
    observeCart();

    if (window.PUB_SUB_EVENTS && window.subscribe) {
      try {
        window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, rebindAll);
      } catch (e) {}
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
